import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'

import PubSub from '@paytrace/pt-skel/Event/PubSub'
import * as React from 'react'
import { useContext, useEffect, useState } from 'react'
import Event from '../../../Event'

import { TransactionSearchRecord } from '../../../types'
import { ApprovedView } from './ApprovedView'
import { DeclinedView } from './DeclinedView'
import { PartiallyApprovedView } from './PartiallyApprovedView'

import './TransactionPage.css'
import { eligibleCardForEnhancedData } from '../../../Helper/EnhancedData/EnhancedDataUtils'

export interface TransactionPageProps {
  transaction_id: number
  response_code: number
  transaction_identifier: string
  response_message: string
  approval_code: string
  approval_message: string
  amount: number
  include_surcharge: boolean
  emails: string[]
  merchant: number
  customer_id: number
  showEnhancedData: boolean
  onTransactionReprocess: () => void
}

export interface SurchargeAmounts {
  transactionAmount?: number
  surchargeAmount?: number
  totalAmount?: number
}

const APPROVED_RESPONSE_CODES = {
  APPROVED: 101,
  REFUNDED: 106,
  CAPTURED: 112,
  FORCED: 115,
  VOIDED: 109,
}

export function ResponsePage (props: TransactionPageProps) {
  const appContext = useContext<AppCtxI>(AppCtx)
  const orderId = String(props.transaction_id)

  const [transactionRecord, setTransactionRecord] = useState<TransactionSearchRecord | null>(null)

  const isApprovedCode = Object.values(APPROVED_RESPONSE_CODES).includes(props.response_code)
  const requested_amount = transactionRecord?.requested_amount ?? props.amount

  useEffect(() => {
    if (isApprovedCode || props.include_surcharge) {
      PubSub.pub(Event.GET_TRANSACTION_BY_ID_REQUEST, { transactionId: props.transaction_id, merchantId: props.merchant })
    }
  }, [props.merchant, props.include_surcharge, props.transaction_id, isApprovedCode])

  useEffect(() => {
    PubSub.sub(self, Event.GET_TRANSACTION_BY_ID_RESPONSE, (response: TransactionSearchRecord) => {
      setTransactionRecord(response)
    })
    return () => PubSub.unsub(self)
  }, [])

  const settleAmount = transactionRecord?.settle_amount ?? requested_amount

  const isPartiallyApproved = isApprovedCode && settleAmount < requested_amount
  const isApproved = isApprovedCode && !isPartiallyApproved

  const surchargeAmounts = {
    transactionAmount: transactionRecord?.requested_amount,
    surchargeAmount: transactionRecord?.surcharge_amount || 0.0,
    totalAmount: transactionRecord?.settle_amount,
  }

  const cardLevel = transactionRecord?.card_level
  const cardBrand = transactionRecord?.card_brand
  let eligibleCard: boolean = false
  if (cardLevel && cardBrand) {
    eligibleCard = eligibleCardForEnhancedData(cardLevel, cardBrand)
  }

  const isEDV2Enabled: boolean = appContext.records.merchant.is_edv2_enabled || false

  const showEnhancedData = isEDV2Enabled && eligibleCard
  const updatedProps = {
    ...props,
    showEnhancedData,
    amount: settleAmount,
  }

  if (isApproved) {
    return <ApprovedView props={updatedProps} orderId={orderId} surchargeAmounts={surchargeAmounts} />
  } else if (isPartiallyApproved) {
    return <PartiallyApprovedView props={updatedProps} settleAmount={settleAmount} orderId={orderId} surchargeAmounts={surchargeAmounts} />
  }
  return <DeclinedView props={props} orderId={orderId} />
}
